import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import RecruitmentInsightsService, { TeamRequisitionSummary } from '../../services/RecruitmentInsightsService';

const PanelContainer = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  overflow: hidden;
  box-shadow: ${Shadows.sm};
`;

const PanelHeader = styled.div`
  background: linear-gradient(90deg, ${Colors.primary}10 0%, ${Colors.surface} 100%);
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PanelTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const TitleIcon = styled.span`
  font-size: ${FontSizes.xl}px;
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: ${Colors.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primary}10;
  }
`;

const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid ${Colors.borderLight};
`;

const StatItem = styled.div`
  padding: ${Spacing.md}px;
  text-align: center;
  border-right: 1px solid ${Colors.borderLight};
  
  &:last-child {
    border-right: none;
  }
`;

const StatValue = styled.div`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RequisitionsList = styled.div`
  padding: ${Spacing.md}px;
  max-height: 400px;
  overflow-y: auto;
`;

const RequisitionCard = styled.div`
  background: ${Colors.background};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.md}px;
  margin-bottom: ${Spacing.md}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.primary}30;
    transform: translateX(2px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReqHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${Spacing.sm}px;
`;

const ReqTitle = styled.h4`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 8px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background: ${props => {
    switch (props.status) {
      case 'OPEN': return Colors.success + '20';
      case 'FILLED': return Colors.info + '20';
      case 'ON_HOLD': return Colors.warning + '20';
      default: return Colors.borderLight;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'OPEN': return Colors.success;
      case 'FILLED': return Colors.info;
      case 'ON_HOLD': return Colors.warning;
      default: return Colors.textSecondary;
    }
  }};
`;

const ReqMeta = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.sm}px;
`;

const StageProgress = styled.div`
  display: flex;
  gap: ${Spacing.xs}px;
  margin-bottom: ${Spacing.sm}px;
`;

const StageBar = styled.div<{ stage: string; value: number; total: number }>`
  flex: 1;
  height: 20px;
  background: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.xs}px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '${props => props.value}';
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${FontSizes.xs}px;
    font-weight: ${FontWeights.semibold};
    color: ${props => props.value > 0 ? Colors.textOnPrimary : Colors.textTertiary};
    z-index: 2;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.total > 0 ? (props.value / props.total) * 100 : 0}%;
    background: ${props => {
      switch (props.stage) {
        case 'applied': return Colors.info;
        case 'screening': return Colors.warning;
        case 'interview': return Colors.primary;
        case 'offer': return Colors.success;
        default: return Colors.borderDark;
      }
    }};
    transition: width 0.3s ease;
  }
`;

const ActionItems = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  flex-wrap: wrap;
`;

const ActionBadge = styled.div<{ type: 'interview' | 'review' | 'decision' }>`
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${props => {
    switch (props.type) {
      case 'interview': return Colors.primary + '10';
      case 'review': return Colors.warning + '10';
      case 'decision': return Colors.danger + '10';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'interview': return Colors.primary;
      case 'review': return Colors.warning;
      case 'decision': return Colors.danger;
    }
  }};
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const EmptyState = styled.div`
  padding: ${Spacing.xl}px;
  text-align: center;
  color: ${Colors.textSecondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${Spacing.md}px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: ${FontSizes.md}px;
  margin-bottom: ${Spacing.sm}px;
`;

const EmptySubtext = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
`;

const DataSourceLabel = styled.div`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${Colors.backgroundDark};
  border-top: 1px solid ${Colors.borderLight};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LastUpdatedLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const UpdateDot = styled.span<{ fresh: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.fresh ? Colors.success : Colors.warning};
`;

interface HiringTeamPanelProps {
  managerId?: string;
  department: string;
  className?: string;
}

const HiringTeamPanel: React.FC<HiringTeamPanelProps> = ({
  managerId,
  department,
  className
}) => {
  const navigate = useNavigate();
  const currentRole = useSelector((state: RootState) => state.user.currentRole);
  const userName = useSelector((state: RootState) => state.user.userName);
  
  const [requisitions, setRequisitions] = useState<TeamRequisitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Use userName as managerId if not provided
  const effectiveManagerId = managerId || userName;

  useEffect(() => {
    if (currentRole === 'MANAGER') {
      loadManagerData();
    }
  }, [currentRole, effectiveManagerId, department]);

  const loadManagerData = async () => {
    setLoading(true);
    try {
      const data = await RecruitmentInsightsService.getManagerInsights(
        effectiveManagerId,
        department
      );
      setRequisitions(data.requisitions);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load manager hiring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequisitionClick = (reqId: string) => {
    navigate(`/requisitions/${reqId}`, {
      state: {
        fromPanel: 'hiring_team',
        managerId: effectiveManagerId,
        department
      }
    });
  };

  const handleViewAll = () => {
    navigate('/requisitions?view=team', {
      state: {
        fromPanel: 'hiring_team',
        managerId: effectiveManagerId,
        department
      }
    });
  };

  // Calculate summary stats
  const openReqs = requisitions.filter(r => r.status === 'OPEN').length;
  const totalCandidates = requisitions.reduce((sum, r) => sum + r.candidateCount, 0);
  const pendingActions = requisitions.reduce((sum, r) => 
    sum + r.nextActions.reduce((actionSum, a) => actionSum + a.count, 0), 0
  );

  const formatTimeDiff = (date: Date): string => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const isFresh = (Date.now() - lastUpdated.getTime()) < 4 * 60 * 60 * 1000;

  // Don't show panel for non-managers
  if (currentRole !== 'MANAGER') {
    return null;
  }

  return (
    <PanelContainer className={className}>
      <PanelHeader>
        <PanelTitle>
          Hiring in My Team
        </PanelTitle>
        <ViewAllLink onClick={handleViewAll}>
          View All →
        </ViewAllLink>
      </PanelHeader>

      <QuickStats>
        <StatItem>
          <StatValue>{openReqs}</StatValue>
          <StatLabel>Open Positions</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{totalCandidates}</StatValue>
          <StatLabel>Total Candidates</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{pendingActions}</StatValue>
          <StatLabel>Pending Actions</StatLabel>
        </StatItem>
      </QuickStats>

      <RequisitionsList>
        {loading ? (
          <EmptyState>
            <EmptyText>Loading hiring data...</EmptyText>
          </EmptyState>
        ) : requisitions.length === 0 ? (
          <EmptyState>
            <EmptyIcon></EmptyIcon>
            <EmptyText>No Active Requisitions</EmptyText>
            <EmptySubtext>
              You don't have any open positions in your team at the moment
            </EmptySubtext>
          </EmptyState>
        ) : (
          requisitions.map(req => {
            const totalInPipeline = req.stageCount.applied + 
              req.stageCount.screening + 
              req.stageCount.interview + 
              req.stageCount.offer;

            return (
              <RequisitionCard 
                key={req.requisitionId}
                onClick={() => handleRequisitionClick(req.requisitionId)}
              >
                <ReqHeader>
                  <ReqTitle>{req.title}</ReqTitle>
                  <StatusBadge status={req.status}>
                    {req.status}
                  </StatusBadge>
                </ReqHeader>

                <ReqMeta>
                  <span>{req.daysOpen} days open</span>
                  <span>{req.candidateCount} candidates</span>
                </ReqMeta>

                <StageProgress>
                  <StageBar 
                    stage="applied" 
                    value={req.stageCount.applied}
                    total={totalInPipeline}
                  />
                  <StageBar 
                    stage="screening" 
                    value={req.stageCount.screening}
                    total={totalInPipeline}
                  />
                  <StageBar 
                    stage="interview" 
                    value={req.stageCount.interview}
                    total={totalInPipeline}
                  />
                  <StageBar 
                    stage="offer" 
                    value={req.stageCount.offer}
                    total={totalInPipeline}
                  />
                </StageProgress>

                {req.nextActions.length > 0 && (
                  <ActionItems>
                    {req.nextActions.map((action, idx) => (
                      <ActionBadge key={idx} type={action.type}>
                        {action.type === 'interview' && ''}
                        {action.type === 'review' && ''}
                        {action.type === 'decision' && '✓'}
                        {action.count} {action.type}
                        {action.dueDate && ` (${Math.ceil((action.dueDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))}d)`}
                      </ActionBadge>
                    ))}
                  </ActionItems>
                )}
              </RequisitionCard>
            );
          })
        )}
      </RequisitionsList>

      <DataSourceLabel>
        <span>Source: Recruitment Data – {department}</span>
        <LastUpdatedLabel>
          <UpdateDot fresh={isFresh} />
          Updated {formatTimeDiff(lastUpdated)}
        </LastUpdatedLabel>
      </DataSourceLabel>
    </PanelContainer>
  );
};

export default HiringTeamPanel;